/**
 * Verified YouTube playlist IDs for Lumo Project Gospel films.
 *
 * Sources: direct playlist URLs from YouTube search titled results — NOT
 * AI-summarised text (which can hallucinate IDs). All IDs begin with
 * `PLcJVIuhI8is`, the Lumo Project channel's playlist prefix
 * (channel: UCjiDMt6Lm1jYQvSp4BROWVw / @TheLumoProject).
 *
 * Confidence tiers used during research:
 *   HIGH  — ID appeared as a titled link in search results (included here)
 *   MEDIUM — ID appeared only in search summary text (excluded; use fallback)
 *
 * Missing books (—) within a language fall back to Lumo channel search.
 * Languages absent from this map entirely fall back to @FCBHGospelFilm search.
 *
 * To add entries: verify the playlist URL on YouTube directly, then add
 * the 34-char `PL…` ID. Do not add IDs from AI summaries without verification.
 */

export type GospelBook = 'MAT' | 'MRK' | 'LUK' | 'JHN';

/** ISO 639-3 → book → YouTube playlist ID */
export const LUMO_PLAYLISTS: Record<string, Partial<Record<GospelBook, string>>> = {
  // English — all 4 verified
  eng: {
    MAT: 'PLcJVIuhI8isJJgv2R7PgPTFK5hpZSwckj',
    MRK: 'PLcJVIuhI8isJMzXK9iJ_UhJrcRu7lgtiN',
    LUK: 'PLcJVIuhI8isJOKcvkIMhwj7Gv5769FqEJ',
    JHN: 'PLcJVIuhI8isK1RYcLxY0L929cnN3vALi8',
  },
  // Spanish — all 4 verified
  spa: {
    MAT: 'PLcJVIuhI8isLR39OxYEOU_tm8nefL_2Bd',
    MRK: 'PLcJVIuhI8isIrYrCSh-h8ZKfpTJz_-buN',
    LUK: 'PLcJVIuhI8isI_haseMB0LuqZiRDb_p7lt',
    JHN: 'PLcJVIuhI8isLpaPKbn34Tnx64v4cueJzn',
  },
  // French — all 4 verified
  fra: {
    MAT: 'PLcJVIuhI8isLnKZiagb1505ZUgLZsv_8R',
    MRK: 'PLcJVIuhI8isLMXS0yB8ByIKWYM4U6mP9s',
    LUK: 'PLcJVIuhI8isLrj8Ak8yHHuKUOt8jMuWnX',
    JHN: 'PLcJVIuhI8isKzhrkkvHZvhtYx_6cMMMoo',
  },
  // Portuguese — MRK/LUK medium confidence only; MAT+JHN verified
  por: {
    MAT: 'PLcJVIuhI8isIHL_P7D5yBciIPV_hQipAz',
    JHN: 'PLcJVIuhI8isI2GDUjwetaGXcj96cXikwY',
  },
  // German — MRK exists but playlist not surfaced
  deu: {
    MAT: 'PLcJVIuhI8isIbDTaPl1XCRNw5WcGaQ1Cv',
    LUK: 'PLcJVIuhI8isL8vbdiLgwtOZ9lQQDfS1Bu',
    JHN: 'PLcJVIuhI8isKbGRwQFF_7i2Y1YszLCejX',
  },
  // Italian — all 4 verified
  ita: {
    MAT: 'PLcJVIuhI8isK089vPBQcgYI5Xe5XyMtRM',
    MRK: 'PLcJVIuhI8isJUv8_3n27k6Xnh3ZuGWi6l',
    LUK: 'PLcJVIuhI8isIDKK0Bpy5NK7D07q0xH36Y',
    JHN: 'PLcJVIuhI8isLPedFbudt_siPrwEXGVPdT',
  },
  // Arabic — MRK/LUK medium confidence only; MAT+JHN verified
  ara: {
    MAT: 'PLcJVIuhI8isKETk-thSd1C4CGuUbBRdTl',
    JHN: 'PLcJVIuhI8isKr8s2aaaF0qhM27QMzbX8m',
  },
  // Hindi — MRK/JHN medium confidence; MAT+LUK verified
  hin: {
    MAT: 'PLcJVIuhI8isK0PzHe3MHKB88rLf9C9FMM',
    LUK: 'PLcJVIuhI8isJS8RHzEqqU7j5UDEW_jb7q',
  },
  // Russian — MAT/MRK/LUK medium confidence; JHN verified
  rus: {
    JHN: 'PLcJVIuhI8isI-T0P0gYP7U5z0NaZWXLaL',
  },
  // Mandarin Chinese — MAT not found; MRK/LUK/JHN verified
  cmn: {
    MRK: 'PLcJVIuhI8isIFWh7YJpkcq-7ACOpxh5TT',
    LUK: 'PLcJVIuhI8isK17xc0xw-5RcEah7YI-WbK',
    JHN: 'PLcJVIuhI8isKc8xN_xJtG11EyluYFD2RZ',
  },
  // Turkish — MRK/LUK/JHN medium confidence; MAT verified
  tur: {
    MAT: 'PLcJVIuhI8isJTNRznhCKKVoKYMAQvP30P',
  },
  // Polish — MRK/LUK/JHN not surfaced; MAT verified
  pol: {
    MAT: 'PLcJVIuhI8isIk6k68f6ugVj-ZcYTFtOH9',
  },
  // Romanian — MRK/LUK/JHN medium confidence; MAT verified
  ron: {
    MAT: 'PLcJVIuhI8isLP5GLatXC023etttYSLDMq',
  },
  // Korean — MAT/JHN medium confidence; MRK+LUK verified
  kor: {
    MRK: 'PLcJVIuhI8isIMf2mcaF0xBSZ24KFUCMBy',
    LUK: 'PLcJVIuhI8isKwte2XLwdJL7UqGZgt0rDL',
  },
  // Indonesian — MRK/LUK/JHN medium confidence; MAT verified
  ind: {
    MAT: 'PLcJVIuhI8isIFjuPHcwxDwLYkWHF2eoDJ',
  },
  // Vietnamese — MAT not found; MRK/LUK/JHN verified
  vie: {
    MRK: 'PLcJVIuhI8isIsPgUYIowC3oMnRy8IsywN',
    LUK: 'PLcJVIuhI8isLSb4k8Ffw0D40mSyFlSrSG',
    JHN: 'PLcJVIuhI8isJCsVYHapEbg6hTKzhZfgW8',
  },
  // Farsi/Persian — MRK/LUK medium confidence; MAT verified
  fas: {
    MAT: 'PLcJVIuhI8isJvsO-klfYJraI2DbKOKRzg',
  },
  // Urdu — MRK/JHN medium confidence; MAT+LUK verified
  urd: {
    MAT: 'PLcJVIuhI8isJLLApF8Je5DC5TFA0qy6X3',
    LUK: 'PLcJVIuhI8isI0loJQpNnd_4XdNvHqmwB2',
  },
  // Thai — MRK/LUK playlists not surfaced; MAT verified
  tha: {
    MAT: 'PLcJVIuhI8isI1aG9lOvUvJBYsUP8HI1IY',
  },
  // Swahili — LUK not found; MAT+JHN verified (MRK medium confidence)
  swa: {
    MAT: 'PLcJVIuhI8isJZ5W1VtKlePOpJ5bkrXIrs',
    JHN: 'PLcJVIuhI8isJWWAQ9cEQtiGw608v9ICGQ',
  },
  // Afrikaans — all 4 verified
  afr: {
    MAT: 'PLcJVIuhI8isKadew9yKhF_5pryVlFEvyY',
    MRK: 'PLcJVIuhI8isIToK-IMsvDZavdm9aePrR9',
    LUK: 'PLcJVIuhI8isJmigadk3LOo3tHmaTlCYsX',
    JHN: 'PLcJVIuhI8isLsuY1-RMblMcG7AnO4-OAN',
  },
  // Bengali — LUK/JHN not surfaced; MAT+MRK verified
  ben: {
    MAT: 'PLcJVIuhI8isK-52gd7kjAFyGm0h3It_1z',
    MRK: 'PLcJVIuhI8isIk72dVwE7FAflFS_M4Y4Mz',
  },
  // Malay — MRK not surfaced; MAT+JHN verified
  msa: {
    MAT: 'PLcJVIuhI8isJnxKamLcxp95uNiMv5xvw8',
    JHN: 'PLcJVIuhI8isJA_1bG0zQ0-P0PK3YIRCJj',
  },
  // Malayalam — MRK/LUK medium confidence; MAT verified
  mal: {
    MAT: 'PLcJVIuhI8isLQWZXsTQFSZ6ek8Bor2-ep',
  },
  // Telugu — MAT only verified; others not surfaced
  tel: {
    MAT: 'PLcJVIuhI8isKAj0nCGCa4uTAX0A3cTFXC',
  },
  // Amharic — MRK/LUK medium confidence; MAT+JHN verified
  amh: {
    MAT: 'PLcJVIuhI8isIaqFt-Lhwy7UW49xFo8s45',
    JHN: 'PLcJVIuhI8isKosQAZI0awjxp0-8GY6jtd',
  },
  // Dutch — LUK medium confidence; MAT+MRK+JHN verified
  nld: {
    MAT: 'PLcJVIuhI8isJonDn84O2aGiREowhTC5sz',
    MRK: 'PLcJVIuhI8isJaVDwiWyr4IxFfgdMKL-m6',
    JHN: 'PLcJVIuhI8isIuuj4BfxYxLyF9RTah5ifY',
  },
  // Japanese — MRK/LUK not found; MAT+JHN verified
  jpn: {
    MAT: 'PLcJVIuhI8isLaUvLPTqHWyR8djlaTyrfz',
    JHN: 'PLcJVIuhI8isLVvpNX2pftB5PjVbQYNhIW',
  },
  // Zulu — MAT+MRK returned same ID (suspect); JHN verified
  zul: {
    JHN: 'PLcJVIuhI8isJTNYof0sOT85R5HpMjjqeZ',
  },
};

/**
 * ISO 639-3 codes confirmed to have content on the Lumo Project channel.
 * Used to decide whether the channel-search fallback goes to @TheLumoProject
 * or @FCBHGospelFilm when no direct playlist ID is available.
 */
export const LUMO_CHANNEL_LANGUAGES = new Set(Object.keys(LUMO_PLAYLISTS));
